import * as React from 'react'
import cx from 'classnames'
import { ControlStatus } from '../ControlStatus'
import { NumericInputHookConfig, useNumericInput } from 'hooks/numeric'
import { useObservableFocus, useFocusTracking } from 'hooks/focus-hooks'
import styles from './NumericInput.sass'
import { NEVER, Observable } from 'rxjs'
import { getCurrencyCode } from 'utils/money-util'

export interface NumericInputProps
  extends Omit<
      React.InputHTMLAttributes<HTMLInputElement>,
      'value' | 'onChange'
    >,
    NumericInputHookConfig {
  currencyCode?: string
  status?: ControlStatus
  focus$?: Observable<unknown>
  isNotValid?: boolean
  dataTest?: string
}

export default function NumericInput({
  className,
  value,
  onChange,
  precision = 0,
  locale,
  useGrouping,
  onBlur,
  focus$ = NEVER,
  status = 'default',
  mode = 'live',
  currencyCode,
  isNotValid,
  dataTest,
  ...props
}: NumericInputProps) {
  const inputRef = React.useRef<HTMLInputElement | null>(null)
  useObservableFocus(focus$, inputRef)
  const connector = useNumericInput({
    value,
    precision,
    mode,
    locale,
    useGrouping,
    onChange,
  })
  const blur = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (mode === 'draft') {
        connector.commit()
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [onBlur, mode, connector.commit],
  )
  const [focusProps, isFocused] = useFocusTracking({
    onBlur: blur,
    onFocus: props.onFocus,
  })

  const keyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      connector.keyDownPreventNonNumeric(e)
      if (e.key === 'Enter' && mode === 'draft') {
        connector.commit()
      }
    },
    [connector],
  )

  return (
    <div
      className={cx(
        styles.input,
        props.disabled && styles.disabled,
        isFocused && styles.focused,
        isNotValid && styles.isNotValid,
        styles[status],
      )}
    >
      {currencyCode && (
        <div className={styles.currencySymbol}>
          {getCurrencyCode(currencyCode)}
        </div>
      )}
      <input
        className={cx(styles.inner, className)}
        data-test={dataTest}
        type="text"
        ref={inputRef}
        {...props}
        value={connector.value}
        onChange={(e) => connector.onChange(e.target.value)}
        {...focusProps}
        onKeyDown={keyDown}
      />
    </div>
  )
}
